import React, { useCallback, useEffect, useState } from 'react';
import { Battery, Signal, Wifi, Sun, Moon, PhoneIncoming } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
interface MobileFrameProps {
  children: React.ReactNode;
  className?: string;
  onSimulateCall?: () => void;
}
const PHONE_WIDTH = 390;
const PHONE_HEIGHT = 844;
export function MobileFrame({
  children,
  className = '',
  onSimulateCall
}: MobileFrameProps) {
  const {
    isLight,
    toggleTheme
  } = useTheme();
  const [scale, setScale] = useState(1);
  const calculateScale = useCallback(() => {
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    // Reserve padding around the phone (48px top/bottom, 200px right for controls, 48px left)
    const availableHeight = vh - 96;
    const availableWidth = vw - 280;
    const scaleByHeight = availableHeight / PHONE_HEIGHT;
    const scaleByWidth = availableWidth / PHONE_WIDTH;
    // Use the smaller scale, capped at 1 (never larger than native)
    const newScale = Math.min(scaleByHeight, scaleByWidth, 1);
    // Minimum scale to prevent it from getting too tiny
    setScale(Math.max(newScale, 0.45));
  }, []);
  useEffect(() => {
    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, [calculateScale]);
  return <div style={{
    height: '100dvh'
  }} className={`w-full flex items-center justify-center font-sans transition-colors duration-500 ${isLight ? 'bg-stone-100 text-slate-900' : 'bg-black text-white'}`} data-id="element-342">
      {/* Right-side Controls — positioned relative to viewport */}
      <div style={{
      position: 'absolute',
      right: 'clamp(12px, 3vw, 32px)',
      top: '50%',
      transform: 'translateY(-50%)'
    }} className="flex flex-col items-center gap-5 z-50" data-id="element-343">
        {/* Theme Toggle */}
        <div className="flex flex-col items-center gap-1.5" data-id="element-344">
          <button onClick={toggleTheme} className={`p-2.5 rounded-full shadow-lg transition-all ${isLight ? 'bg-white text-slate-700 hover:bg-slate-50 ring-1 ring-slate-200' : 'bg-zinc-900 text-zinc-300 hover:bg-zinc-800 ring-1 ring-white/10'}`} data-id="element-345">
            {isLight ? <Moon className="w-4 h-4" data-id="element-346" /> : <Sun className="w-4 h-4" data-id="element-347" />}
          </button>
          <span className={`text-[10px] font-medium ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-348">
            {isLight ? 'Dark' : 'Light'}
          </span>
        </div>

        {/* Simulate Call Button */}
        {onSimulateCall && <div className="flex flex-col items-center gap-1.5" data-id="element-349">
            <button onClick={onSimulateCall} className={`p-2.5 rounded-full shadow-lg transition-all ${isLight ? 'bg-white text-vexu-700 hover:bg-slate-50 ring-1 ring-slate-200' : 'bg-zinc-900 text-vexu hover:bg-zinc-800 ring-1 ring-white/10'}`} data-id="element-350">
              <PhoneIncoming className="w-4 h-4" data-id="element-351" />
            </button>
            <span className={`text-[10px] font-medium ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-352">
              Simulate
            </span>
          </div>}
      </div>

      {/* Scaled Phone Container */}
      <div style={{
      width: PHONE_WIDTH,
      height: PHONE_HEIGHT,
      transform: `scale(${scale})`,
      transformOrigin: 'center center',
      flexShrink: 0
    }} data-id="element-353">
        {/* Phone Frame — outer shell */}
        <div className={`relative w-full h-full shadow-2xl rounded-[40px] border-[8px] transition-colors duration-500 ${isLight ? 'bg-[#e8e8e6] border-[#e8e8e6] ring-1 ring-black/[0.04] shadow-black/[0.08]' : 'bg-zinc-900 border-zinc-900 ring-1 ring-white/10'}`} data-id="element-354">
          {/* Inner Screen Mask — clips all content to the inner rounded area */}
          <div className="absolute inset-0 rounded-[32px] overflow-hidden" data-id="element-355">
            {/* Status Bar */}
            <div className={`absolute top-0 left-0 right-0 h-12 px-6 flex items-center justify-between z-50 transition-colors duration-500 ${isLight ? 'text-slate-800 bg-white/60 backdrop-blur-2xl' : 'text-white bg-dark/80 backdrop-blur-2xl border-b border-white/5'}`} data-id="element-356">
              <div className="text-sm font-semibold ml-2" data-id="element-357">9:41</div>
              <div className="flex items-center gap-1.5" data-id="element-358">
                <Signal className="w-4 h-4" data-id="element-359" />
                <Wifi className="w-4 h-4" data-id="element-360" />
                <Battery className="w-5 h-5" data-id="element-361" />
              </div>
            </div>

            {/* Dynamic Island Placeholder */}
            <div className={`absolute top-2 left-1/2 -translate-x-1/2 w-[120px] h-[35px] rounded-full z-50 pointer-events-none transition-colors duration-500 ${isLight ? 'bg-black ring-1 ring-black/5' : 'bg-black ring-1 ring-white/5'}`} data-id="element-362" />

            {/* Content Area */}
            <div className={`w-full h-full overflow-y-auto pt-12 pb-safe transition-colors duration-500 ${isLight ? 'app-gradient-bg-light' : 'app-gradient-bg'} ${className}`} data-id="element-363">
              {children}
            </div>

            {/* Home Indicator */}
            <div className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-32 h-1 rounded-full z-50 pointer-events-none transition-colors duration-500 ${isLight ? 'bg-slate-900/20' : 'bg-white/20'}`} data-id="element-364" />
          </div>
        </div>
      </div>
    </div>;
}